// src/i18n/translations.ts
export type Language = 'en' | 'fr' | 'pl';

const en = {
  nav: {
    home: 'Home',
    about: 'About',
    portfolio: 'Portfolio',
    skills: 'Skills',
    contact: 'Contact',
  },
  home: {
    available: 'Open to opportunities',
    greeting: 'Welcome to my portfolio',
    location: 'Paris, France · Equans (Bouygues)',
    viewWork: 'View My Work',
    getInTouch: 'Get in Touch',
    stats: [
      { value: '30+', label: 'Projects built' },
      { value: '5 yrs', label: 'Experience' },
      { value: '3', label: 'Languages' },
    ],
  },
  about: {
    sectionLabel: '02 — About',
    title: 'About',
    titleHighlight: 'Me',
    subtitle: 'Scroll through my journey — from studies to professional experience.',
    bioRole: 'Full-Stack Developer & Blockchain Engineer · Paris, France',
    bioText: "Currently working as a Full-Stack Developer at Equans (Bouygues Energies & Services), building digitalization software for engineering processes. Master's in Blockchain Engineering from ESGI Paris.",
    bioLangs: '🇫🇷 French · 🇵🇱 Polish · 🇬🇧 English (TOEIC 850)',
    statsLabels: ['Years experience', 'Projects built', 'Languages'],
  },
  portfolio: {
    sectionLabel: '03 — Portfolio',
    title: 'My',
    titleHighlight: 'Projects',
    subtitle: "A collection of projects I've built — from decentralized apps to full-stack platforms.",
    filterCategory: 'Category',
    filterYear: 'Year',
    results: (n: number) => `${n} project${n !== 1 ? 's' : ''} shown`,
    all: 'All',
    featured: 'Featured',
    viewLive: 'Live Demo',
    viewGithub: 'GitHub',
    allProjects: 'All projects',
    categories: { Personal: 'Personal', Work: 'Work', School: 'School' },
  },
  skills: {
    sectionLabel: '04 — Skills',
    title: 'My',
    titleHighlight: 'Skills',
    subtitle: 'Technologies and tools I work with, from frontend to blockchain.',
    levels: { Expert: 'Expert', Advanced: 'Advanced', Intermediate: 'Intermediate', Basic: 'Basic' },
  },
  contact: {
    sectionLabel: '05 — Contact',
    title: "Let's Work",
    titleHighlight: 'Together',
    subtitle: "Whether you have an opportunity, a project in mind, or simply want to connect — I'm always open to a conversation.",
    available: 'Open to new opportunities',
    location: 'Paris, France — Available remotely & on-site',
    message: "I'm available for full-time positions, freelance missions, and exciting collaborations in full-stack development and blockchain. Let's build something great.",
    emailCta: 'Send me an email',
    emailLabel: 'Email',
    linkedinLabel: 'LinkedIn',
    githubLabel: 'GitHub',
  },
};

const fr: typeof en = {
  nav: {
    home: 'Accueil',
    about: 'Parcours',
    portfolio: 'Projets',
    skills: 'Compétences',
    contact: 'Contact',
  },
  home: {
    available: 'Ouvert aux opportunités',
    greeting: 'Bienvenue sur mon portfolio',
    location: 'Paris, France · Equans (Bouygues)',
    viewWork: 'Voir mes projets',
    getInTouch: 'Me contacter',
    stats: [
      { value: '30+', label: 'Projets réalisés' },
      { value: '5 ans', label: "D'expérience" },
      { value: '3', label: 'Langues' },
    ],
  },
  about: {
    sectionLabel: '02 — Parcours',
    title: 'Mon',
    titleHighlight: 'Parcours',
    subtitle: 'Faites défiler mon parcours — de mes études à mon expérience professionnelle.',
    bioRole: 'Développeur Full-Stack & Ingénieur Blockchain · Paris, France',
    bioText: "Développeur Full-Stack chez Equans (Bouygues Energies & Services), je travaille sur la digitalisation de processus. Diplômé d'un Master Expert Blockchain à l'ESGI Paris.",
    bioLangs: '🇫🇷 Français · 🇵🇱 Polonais · 🇬🇧 Anglais (TOEIC 850)',
    statsLabels: ["Ans d'expérience", 'Projets réalisés', 'Langues'],
  },
  portfolio: {
    sectionLabel: '03 — Projets',
    title: 'Mes',
    titleHighlight: 'Projets',
    subtitle: 'Une collection de projets — des dApps aux plateformes full-stack.',
    filterCategory: 'Catégorie',
    filterYear: 'Année',
    results: (n: number) => `${n} projet${n > 1 ? 's' : ''} affiché${n > 1 ? 's' : ''}`,
    all: 'Tous',
    featured: 'À la une',
    viewLive: 'Démo live',
    viewGithub: 'GitHub',
    allProjects: 'Tous les projets',
    categories: { Personal: 'Personnel', Work: 'Professionnel', School: 'École' },
  },
  skills: {
    sectionLabel: '04 — Compétences',
    title: 'Mes',
    titleHighlight: 'Compétences',
    subtitle: 'Technologies et outils — du frontend au blockchain.',
    levels: { Expert: 'Expert', Advanced: 'Avancé', Intermediate: 'Intermédiaire', Basic: 'Débutant' },
  },
  contact: {
    sectionLabel: '05 — Contact',
    title: 'Travaillons',
    titleHighlight: 'Ensemble',
    subtitle: "Une opportunité, un projet, ou l'envie d'échanger — je suis toujours disponible.",
    available: 'Ouvert aux nouvelles opportunités',
    location: 'Paris, France — Disponible en remote & sur site',
    message: 'Disponible pour des postes CDI, des missions freelance et des collaborations en développement full-stack et blockchain.',
    emailCta: "M'envoyer un email",
    emailLabel: 'Email',
    linkedinLabel: 'LinkedIn',
    githubLabel: 'GitHub',
  },
};

const pl: typeof en = {
  nav: {
    home: 'Główna',
    about: 'O mnie',
    portfolio: 'Projekty',
    skills: 'Umiejętności',
    contact: 'Kontakt',
  },
  home: {
    available: 'Otwarty na oferty',
    greeting: 'Witaj w moim portfolio',
    location: 'Paryż, Francja · Equans (Bouygues)',
    viewWork: 'Zobacz projekty',
    getInTouch: 'Skontaktuj się',
    stats: [
      { value: '30+', label: 'Projektów' },
      { value: '5 lat', label: 'Doświadczenia' },
      { value: '3', label: 'Języki' },
    ],
  },
  about: {
    sectionLabel: '02 — O mnie',
    title: 'O',
    titleHighlight: 'mnie',
    subtitle: 'Przewiń przez moją historię — od studiów do doświadczenia zawodowego.',
    bioRole: 'Full-Stack Developer & Inżynier Blockchain · Paryż, Francja',
    bioText: 'Pracuję jako Full-Stack Developer w Equans (Bouygues Energies & Services), tworząc oprogramowanie do digitalizacji procesów. Magister Inżynierii Blockchain z ESGI Paris.',
    bioLangs: '🇫🇷 Francuski · 🇵🇱 Polski · 🇬🇧 Angielski (TOEIC 850)',
    statsLabels: ['Lata doświadczenia', 'Projektów', 'Języki'],
  },
  portfolio: {
    sectionLabel: '03 — Projekty',
    title: 'Moje',
    titleHighlight: 'Projekty',
    subtitle: 'Kolekcja projektów — od zdecentralizowanych aplikacji po platformy full-stack.',
    filterCategory: 'Kategoria',
    filterYear: 'Rok',
    results: (n: number) => `${n} projekt${n === 1 ? '' : n < 5 ? 'y' : 'ów'} wyświetlono`,
    all: 'Wszystkie',
    featured: 'Wyróżnione',
    viewLive: 'Demo na żywo',
    viewGithub: 'GitHub',
    allProjects: 'Wszystkie projekty',
    categories: { Personal: 'Osobiste', Work: 'Praca', School: 'Szkoła' },
  },
  skills: {
    sectionLabel: '04 — Umiejętności',
    title: 'Moje',
    titleHighlight: 'Umiejętności',
    subtitle: 'Technologie i narzędzia — od frontendu po blockchain.',
    levels: { Expert: 'Ekspert', Advanced: 'Zaawansowany', Intermediate: 'Średnio', Basic: 'Podstawowy' },
  },
  contact: {
    sectionLabel: '05 — Kontakt',
    title: 'Pracujmy',
    titleHighlight: 'Razem',
    subtitle: 'Masz projekt, ofertę lub chcesz porozmawiać? Zawsze jestem do dyspozycji.',
    available: 'Otwarty na nowe oferty',
    location: 'Paryż, Francja — Dostępny zdalnie i stacjonarnie',
    message: 'Dostępny na stanowiska pełnoetatowe, freelance i współpracę przy projektach full-stack i blockchain.',
    emailCta: 'Wyślij email',
    emailLabel: 'Email',
    linkedinLabel: 'LinkedIn',
    githubLabel: 'GitHub',
  },
};

export const translations = { en, fr, pl };
export type Translations = typeof en;
